import {
  Box, CardMedia, CircularProgress, Divider, Stack, TextField, Typography,
} from '@mui/material';
import DietaryTags from 'components/DietaryTags';
import Overlay from 'components/Overlay';
import CloseButton from 'components/Overlay/CloseButton';
import usePersistedState from 'hooks/usePersistedState';
import { throttle } from 'lodash-es';
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { getItemImage } from 'utils/item';
import { env } from 'constants';
import { modifierContext } from '../ModifierProvider';
import Actions from './Actions';
import Header from './Header';
import ItemAmount from './ItemAmount';
import ItemSizes from './ItemSizes';
import Modifiers from './Modifiers';

function ItemModal({
  item, open, onClose, showSpecialNote, available,
}) {
  const [size, setSize] = useState();
  const [amount, setAmount] = useState(1);
  const [specialReq, setSpecialReq] = usePersistedState('', {
    key: item?.id && `item-modal-${item?.id}/special-req`,
    ttl: 1000 * 60 * 5,
  });
  const [errorVersion, setErrorVersion] = useState();
  const { loading, parent, visibleModifiers } = useContext(modifierContext);
  const [showModifiersError, setShowModifiersError] = useState(false);
  const image = getItemImage(item, 'lg');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleScroll = useCallback(
    throttle((event) => {
      if (image) {
        const { scrollTop } = event.target;
        if (isCollapsed && scrollTop < 300) {
          setIsCollapsed(false);
        } else if (!isCollapsed && scrollTop > 300) {
          setIsCollapsed(true);
        }
      }
    }, 200),
    [isCollapsed],
  );

  useEffect(() => {
    if (open) {
      return;
    }
    setAmount(1);
    setSize();
  }, [open]);

  const onCloseModal = () => {
    setErrorVersion(1);
    setIsCollapsed(false);
    onClose();
  };

  function renderContent() {
    if (!item) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <>
        <Header item={item} isCollapsed={isCollapsed} />
        <Overlay.Content sx={{ p: 0 }} onScroll={handleScroll}>
          {
            image && !parent && (
              <CardMedia
                height="250"
                image={image}
                alt={item.title}
                sx={{ borderRadius: 0, height: 250 }}
              >
                <CloseButton sx={{
                  backgroundColor: 'background.elevation1', position: 'relative', ml: 2, mt: 1.5,
                }}
                />
              </CardMedia>
            )
          }
          {!parent && ((image && item.name) || item.description || item.dietary_tags?.length > 0) && (
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'border.default',
              }}
            >
              <Stack spacing={1}>
                {image && <Typography variant="title-h6">{item.name}</Typography>}
                {
                  item.description && (
                    <Typography variant="body-md" color="text.secondary">
                      {item.description}
                    </Typography>
                  )
                }
                <DietaryTags tags={item.dietary_tags} showLabel />
              </Stack>
            </Box>
          )}
          <Box py={1.5} px={2}>
            {env.IS_CATERING && !parent &&
            <ItemAmount item={item} size={size} amount={amount} onChange={setAmount} />}
            {!parent && <ItemSizes item={item} value={size} onChange={setSize} />}
            <Modifiers errorVersion={errorVersion} showModifiersError={showModifiersError} />
            {showSpecialNote && !parent && !loading && (
              <>
                {visibleModifiers.length > 0 && <Divider sx={{ mb: 2 }} />}
                <Box>
                  <Typography variant="title-lg">Special requests</Typography>
                  <TextField
                    sx={{
                      width: '100%', mt: 1.5, mb: 1, visibility: loading ? 'hidden' : 'visible',
                    }}
                    value={specialReq}
                    multiline
                    onChange={({ target: { value } }) => {
                      setSpecialReq(value);
                    }}
                    placeholder="Add a note (extra sauce, no salt, etc.)"
                  />
                </Box>
              </>
            )}
          </Box>
        </Overlay.Content>
        <Overlay.Actions>
          <Actions
            onError={() => setErrorVersion((ev) => ev + 1)}
            item={item}
            size={size}
            specialReq={specialReq}
            onClose={onCloseModal}
            available={available}
            amount={amount}
            setShowModifiersError={setShowModifiersError}
          />
        </Overlay.Actions>
      </>
    );
  }

  return (
    <Overlay
      onClose={onCloseModal}
      scroll="paper"
      open={open}
      mobileFullScreen
      minHeight="60vh"

    >
      {renderContent()}
    </Overlay>
  );
}

export default ItemModal;
